import { bikeColors, InputType } from 'ai-constants'
import { format } from 'date-fns'
import es from 'date-fns/locale/es'
import { ITemplateInput } from 'interfaces/template'
import parsePhoneNumber from 'libphonenumber-js'
import { DynamicInputSchema, IJSONSchema } from 'services/template.service'
import { staticInputsOptions } from 'source-of-truth/static-input-options'
import { inspectionLimitsStore } from 'stores/inspection-limits.store'
import * as yup from 'yup'
import { ObjectShape } from 'yup/lib/object'
import { SchemaDescription } from 'yup/lib/schema'
import locales from '../source-of-truth/locales.json'
import { hasNestedObjects } from './has-nested-objects'

export const CELLPHONE_CHANNELS = ['wsp', 'sms']

const now = new Date()

export const currentYear = now.getFullYear()

const castToNumber = (val: any) => {
  if (!val) {
    return
  }

  return Number(val)
}

export const mergeSchemas = <T extends ObjectShape>(
  ...schemas: yup.ObjectSchema<T>[]
) => {
  const [first, ...rest] = schemas

  const merged = rest.reduce(
    (mergedSchemas, schema) => mergedSchemas.concat(schema),
    first,
  )

  return merged
}

const optionsTestValidator = (
  opts: any[],
): yup.TestFunction<any | undefined, Record<any, any>> => {
  return function (value, ctx) {
    const validations: SchemaDescription = ctx.schema.describe()

    const isRequired = validations.tests.some((ele) => ele.name === 'required')

    if (!value && !isRequired) {
      return true
    }

    if (!value && isRequired) {
      return ctx.createError({ message: 'Campo requerido!' })
    }

    if (!opts.includes(value as string)) {
      return ctx.createError({ message: 'Opción invalida!' })
    }

    return true
  }
}

const vehicleCommonValidations = {
  plate: yup
    .string()
    .matches(/^[a-z0-9]+$/i, 'Unicamente números y letras')
    .min(6, 'Mínimo 6 caracteres.'),
  color: yup.string().test({
    test: optionsTestValidator(staticInputsOptions.car.color),
  }),
  milage: yup.number().transform(castToNumber),
  year: yup
    .number()
    .min(1500, 'Valor mínimo 1500')
    .max(currentYear, `Valor máximo ${currentYear}`)
    .transform(castToNumber),
  use: yup.string().test({
    test: optionsTestValidator(staticInputsOptions.car.use),
  }),
  chassis: yup.string(),
  make: yup.string(),
  model: yup.string().when('make', {
    is: (val) => val,
    then: yup.string().required('Campo requerido!'),
  }),
}

const deliverySchema = {
  disabled: yup.boolean(),
  channel: yup.string().when('disabled', {
    is: false,
    then: (schema) =>
      schema
        .required('Campo requerido!')
        .oneOf(staticInputsOptions.delivery.channel, 'Opción invalida'),
  }),
  destination: yup.string().when(['disabled', 'channel'], {
    is: (disabled, channel) => !disabled && channel === 'email',
    then: (schema) =>
      schema.email('Email no valido').required('Campo requerido!'),
    otherwise: (schema) =>
      schema.test({
        test: (value, schema) => {
          const disabled = schema?.parent?.disabled

          if (disabled) return true

          if (!schema?.parent?.countryISO || !value) return false

          const phone = parsePhoneNumber(value, schema.parent.countryISO)

          if (!phone) return false

          return phone.isValid()
        },
        message: 'Número de celular invalido',
      }),
  }),
  countryISO: yup.string().when(['disabled', 'channel'], {
    is: (disabled, channel) =>
      !disabled && CELLPHONE_CHANNELS.includes(channel),
    then: (schema) => schema.required('Campo requerido!'),
  }),
}

export const yupValidations = {
  consumer: {
    email: yup.string().email('Email invalido'),
    country: yup.string().test({
      test: optionsTestValidator(
        staticInputsOptions.consumer.country.map((c) => c.iso2),
      ),
    }),
    state: yup.string().when('country', {
      is: (value) => value,
      then: yup
        .string()
        .required('Campo requerido!')
        .test({
          test: function (val, ctx) {
            const baseMessage = {
              message:
                'Estado/Provincia no se encuentra dentro del país seleccionado.',
            }

            if (!this.parent.country) {
              return this.createError(baseMessage)
            }

            const isStateValid = staticInputsOptions.consumer.country.map(
              (c) =>
                c.iso2 === this.parent.country &&
                c.states.some((state) => state.state_code === val),
            )

            if (!isStateValid) {
              return this.createError(baseMessage)
            }

            return true
          },
        }),
    }),
    sex: yup.string().test({
      test: optionsTestValidator(staticInputsOptions.consumer.sex),
    }),
    birthdate: yup
      .mixed()
      .test({
        test: function (value, ctx) {
          const validations: SchemaDescription = ctx.schema.describe()

          const isRequired = validations.tests.some(
            (ele) => ele.name === 'required',
          )

          if (!value && isRequired) {
            return ctx.createError({ message: 'Campo requerido!' })
          }

          if (!value && !isRequired) {
            return true
          }

          if (new Date() < new Date(value)) {
            return ctx.createError({
              message: `Fecha maxima ${new Date().toISOString().slice(0, 10)}`,
            })
          }

          return true
        },
      })
      .transform((val) => {
        const convertDate = (dateString: string) => {
          const dateParts = dateString.split('/')

          return new Date(+dateParts[2], dateParts[1] - 1, +dateParts[0])
        }

        try {
          return format(convertDate(val), 'yyyy-MM-dd')
        } catch (err) {
          return null
        }
      }),
    firstName: yup.string(),
    lastName: yup.string(),
    identification: yup.string().matches(/^[0-9]+$/, 'Unicamente números'),
    address: yup.string(),
    city: yup.string(),
    phone: yup.string(),
  },
  goods: {
    serialNumber: yup.string(),
    category: yup.string(),
    make: yup.string(),
    model: yup.string(),
    price: yup.number().transform(castToNumber),
    type: yup.string(),
  },
  bike: {
    serialNumber: yup.string(),
    make: yup.string(),
    model: yup.string(),
    price: yup.number().transform(castToNumber),
    wheelSizes: yup.string().matches(/^[0-9]+$/, 'Unicamente números'),
    color: yup.string().test({
      test: optionsTestValidator(bikeColors),
    }),
  },
  cellphone: {
    serialNumber: yup.string(),
    make: yup.string(),
    model: yup.string(),
    price: yup.number().transform(castToNumber),
    imei: yup
      .string()
      .length(15, 'Debe tener 15 dígitos')
      .matches(/^[0-9]+$/, 'Unicamente números'),
  },
  car: vehicleCommonValidations,
  moto: vehicleCommonValidations,
  machinery: {
    ...vehicleCommonValidations,
    plate: yup.string().test({
      test: function (value, ctx) {
        const validations: SchemaDescription = ctx.schema.describe()

        const isRequired = validations.tests.some(
          (ele) => ele.name === 'required',
        )

        if (!value && isRequired) {
          return ctx.createError({ message: 'Campo requerido!' })
        }

        if (!value && !isRequired) {
          return true
        }

        if ((value || '').length < 5) {
          return this.createError({ message: 'Mínimo 5 caracteres' })
        }

        if (!/^[a-z0-9]+$/i.test(value as string)) {
          return this.createError({ message: 'Unicamente números y letras!' })
        }

        return true
      },
    }),
    type: yup.string().test({
      test: optionsTestValidator(staticInputsOptions.machinery.type),
    }),
    purpose: yup.string().test({
      test: optionsTestValidator(staticInputsOptions.machinery.purpose),
    }),
  },
  delivery: {
    ...deliverySchema,
    redundancy: yup.array().of(
      yup.object({
        channel: deliverySchema.channel,
        countryISO: deliverySchema.countryISO,
        destination: deliverySchema.destination,
      }),
    ),
  },
  locale: yup
    .string()
    .required('Campo requerido!')
    .oneOf(Object.keys(locales), 'Opción invalida!'),
} as const

yup.addMethod(yup.date, 'maxDate', function (maxDate: Date) {
  return this.test('MAX_DATE', (val, ctx) => {
    if (new Date(val) > new Date(maxDate)) {
      return ctx.createError({
        message: `La fecha máxima es ${format(new Date(maxDate), 'P', {
          locale: es,
        })}`,
      })
    }

    return true
  })
})

yup.addMethod(yup.number, 'minDigits', function (minDigits: number) {
  return this.test('MIN_DIGITS', (val, ctx) => {
    const valueCasted = Number(val)
    const digitsCount = Math.trunc(valueCasted).toString().length

    if (digitsCount < minDigits) {
      return ctx.createError({
        message: `Debe tener como mínimo ${minDigits} dígitos`,
      })
    }

    return true
  })
})

yup.addMethod(yup.number, 'maxDigits', function (maxDigits: number) {
  return this.test('MAX_DIGITS', (val, ctx) => {
    const valueCasted = Number(val)
    const digitsCount = Math.trunc(valueCasted).toString().length

    if (digitsCount > maxDigits) {
      return ctx.createError({
        message: `Debe tener como máximo ${maxDigits} dígitos`,
      })
    }

    return true
  })
})

yup.addMethod(yup.date, 'minDate', function (minDate: Date) {
  return this.test('MIN_DATE', (val, ctx) => {
    if (new Date(val) < new Date(minDate)) {
      return ctx.createError({
        message: `La fecha mínima es ${format(new Date(minDate), 'P', {
          locale: es,
        })}`,
      })
    }

    return true
  })
})

yup.addMethod(yup.date, 'maxFileSize', function (maxFileSize: number) {
  return this.test('MAX_FILE_SIZE', (val, ctx) => {
    if (val.size > maxFileSize) {
      return ctx.createError({
        message: `El tamaño máximo del archivo tiene que ser ${
          maxFileSize / 1024 / 1024
        } mb.`,
      })
    }

    return true
  })
})

const yupPropInitializer = (type: InputType): yup.BaseSchema<any, any, any> => {
  switch (type) {
    case 'date':
      return yup.date().typeError('Tiene que ser una fecha!')

    case 'textarea':
    case 'text':
    case 'select':
      return yup.string()

    case 'numeric':
      return yup.number().typeError('Tiene que ser un valor numérico!')

    case 'dialogDecision':
    case 'check':
      return yup.bool()

    case 'checkboxGroup':
    case 'multiselect':
      return yup.array()

    default:
      return yup.mixed()
  }
}

const optionalInputTransformers: Partial<
  Record<InputType, (value: any, originalValue: any) => any>
> = {
  date: (value, originalValue) => {
    if (typeof originalValue === 'string' && !originalValue.trim()) return null
    if (!originalValue) return null

    return value
  },
}

export function createYupSchema(schema: IJSONSchema): yup.ObjectSchema<any> {
  const shape = Object.entries(schema.properties).reduce(
    (acc, [parentObjectProp, childrenProps]) => {
      if (parentObjectProp === 'inputs') {
        return {
          ...acc,
          [parentObjectProp]: yup.object().shape(
            Object.entries(childrenProps as DynamicInputSchema).reduce(
              (
                acc,
                [currKey, currVal]: [
                  string,
                  ITemplateInput & { required: boolean },
                ],
              ) => {
                let yupProp = yupPropInitializer(currVal.type)

                if (currVal.required) {
                  const isMultiSelect =
                    currVal.type === 'multiselect' ||
                    currVal.type === 'checkboxGroup'

                  if (isMultiSelect) {
                    yupProp = (yupProp as yup.ArraySchema<any, any, any>)
                      .required('Campo requerido!')
                      .min(1, 'Mínimo una opción')
                  }

                  if (!isMultiSelect) {
                    yupProp = yupProp.required('Campo requerido!')
                  }
                }

                if (!currVal?.required) {
                  yupProp = yupProp
                    .notRequired()
                    .nullable()
                    //originalValue is the raw value from form input before yup value cast it. That mean's if input type is date, originalValue will be a string like '2026-03-02' and value will be a Date object.
                    .transform((value, originalValue) => {
                      const optionalInputTransformer =
                        optionalInputTransformers[currVal.type]

                      if (optionalInputTransformer) {
                        return optionalInputTransformer(value, originalValue)
                      }

                      if (typeof value === 'boolean') {
                        return value
                      }

                      return !!value ? value : null
                    })
                }

                if (currVal.type === 'file') {
                  yupProp = (yupProp as yup.AnyObjectSchema).test(
                    'maxFileSize',
                    (val, ctx) => {
                      if ((!val || !val?.length) && !currVal?.required)
                        return true

                      if (!val && currVal.required) {
                        return ctx.createError({
                          message: 'Campo requerido!',
                        })
                      }

                      const MAX_FILE_SIZE =
                        inspectionLimitsStore.getState()?.limits.files.size *
                        1024 *
                        1024

                      if ((val as FileList)[0].size > MAX_FILE_SIZE) {
                        return ctx.createError({
                          message: `El archivo no debe pesar mas de ${Math.round(
                            MAX_FILE_SIZE / 1024 / 1024,
                          )}MB.`,
                        })
                      }

                      return true
                    },
                  )
                }

                if (currVal.validationSchema.email) {
                  yupProp = (yupProp as yup.StringSchema).email(
                    'Email invalido',
                  )
                }

                if (currVal?.validationSchema?.minDigits) {
                  yupProp = (yupProp as yup.NumberSchema).minDigits(
                    currVal.validationSchema.minDigits,
                  )
                }

                if (currVal?.validationSchema?.maxDigits) {
                  yupProp = (yupProp as yup.NumberSchema).maxDigits(
                    currVal.validationSchema.maxDigits,
                  )
                }

                if (currVal.validationSchema.maxDate) {
                  yupProp = (yupProp as yup.DateSchema).maxDate(
                    currVal.validationSchema.maxDate,
                  )
                }

                if (currVal.validationSchema.minDate) {
                  yupProp = (yupProp as yup.DateSchema).minDate(
                    currVal.validationSchema.minDate,
                  )
                }

                if (currVal.validationSchema.maxFileSize) {
                  yupProp = (yupProp as yup.DateSchema).maxFileSize(
                    currVal.validationSchema.maxFileSize,
                  )
                }

                if (currVal.validationSchema.maxLength) {
                  yupProp = (yupProp as yup.StringSchema).max(
                    currVal.validationSchema.maxLength,
                    `No mas de ${currVal.validationSchema.maxLength} caracteres.`,
                  )
                }

                if (currVal.validationSchema.minLength) {
                  yupProp = (yupProp as yup.StringSchema).min(
                    currVal.validationSchema.minLength,
                    `No menos de ${currVal.validationSchema.minLength} caracteres.`,
                  )
                }

                if (currVal.validationSchema.maximum) {
                  yupProp = (yupProp as yup.StringSchema).max(
                    currVal.validationSchema.maximum,
                    `El máximo es ${currVal.validationSchema.maximum}`,
                  )
                }

                if (currVal.validationSchema.minimum) {
                  yupProp = (yupProp as yup.StringSchema).min(
                    currVal.validationSchema.minimum,
                    `El mínimo es ${currVal.validationSchema.minimum}`,
                  )
                }

                return {
                  ...acc,
                  [currKey]: yup.object().shape({
                    value: yupProp,
                  }),
                }
              },
              {},
            ),
          ),
        }
      }

      let baseSchema: yup.BaseSchema<any, any, any>

      if (!hasNestedObjects(childrenProps)) {
        baseSchema = yupValidations[parentObjectProp]

        if (childrenProps.required) {
          baseSchema = baseSchema.required('Campo requerido!')
        }

        if (!childrenProps.required) {
          baseSchema = baseSchema
            .notRequired()
            .nullable()
            .transform((value) => (!!value ? value : null))
        }

        return {
          ...acc,
          [parentObjectProp]: baseSchema,
        }
      }

      return {
        ...acc,
        [parentObjectProp]: yup.object().shape(
          Object.entries(childrenProps).reduce(
            (acc, [staticProp, nestedProps]) => {
              baseSchema = yupValidations[parentObjectProp][staticProp]

              if (nestedProps.required) {
                baseSchema = baseSchema.required('Campo requerido!')
              }

              if (!nestedProps.required) {
                baseSchema = baseSchema
                  .notRequired()
                  .nullable()
                  .transform((value) => (!!value ? value : null))
              }

              return {
                ...acc,
                [staticProp]: baseSchema,
              }
            },
            {},
          ),
        ),
      }
    },
    {},
  )

  return yup.object().shape(shape)
}
